import { useCallback, useEffect, useMemo, useState } from "react";
import {
  X,
  MapPin,
  QrCode,
  Building2,
  Layers,
  RefreshCw,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Smartphone,
  Navigation,
} from "lucide-react";
import { useAuthStore } from "../state/auth";
import { useTranslation, type LangCode } from "../i18n";
import { cn } from "../lib/utils";

type TFn = (key: string, fallback?: string) => string;

export interface CheckpointSummary {
  id: string;
  name?: string | null;
  code?: string | null;
  building?: string | null;
  floor?: string | null;
  hospital_name?: string | null;
}

type ScanStatus = "ON_TIME" | "LATE" | "MISSED";

interface HistoryScan {
  id: string;
  scanned_at: string;
  guard_name: string | null;
  guard_id: string | null;
  device_name: string | null;
  scanner_serial: string | null;
  gps_lat: number | null;
  gps_lng: number | null;
  gps_source: "EVENT" | "DEVICE" | "NONE";
  proximity_status: string | null;
  proximity_distance_m: number | null;
  status: ScanStatus;
}

interface HistoryResponse {
  checkpoint: CheckpointSummary & { hospital_name?: string | null };
  scans: HistoryScan[];
  stats: { total: number; on_time: number; late: number; missed: number; compliance: number };
}

interface CheckpointHistoryProps {
  checkpoint: CheckpointSummary;
  onClose: () => void;
}

const EMPTY_STATS = { total: 0, on_time: 0, late: 0, missed: 0, compliance: 0 };

export function CheckpointHistory({ checkpoint, onClose }: CheckpointHistoryProps) {
  const { t, lang } = useTranslation();
  const { user, token } = useAuthStore();

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState<HistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user?.email) return;
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`/api/checkpoints/${checkpoint.id}/history`, window.location.origin);
      if (startDate) url.searchParams.append("start_date", new Date(startDate).toISOString());
      if (endDate) {
        // Include the whole end day by snapping to end-of-day.
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        url.searchParams.append("end_date", end.toISOString());
      }
      const headers: Record<string, string> = { "x-user-email": user.email, Accept: "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch(url.toString(), { headers, cache: "no-store" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      const json = (await res.json()) as HistoryResponse;
      setData(json);
    } catch (e: any) {
      setError(e.message || "Failed to load checkpoint history");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [checkpoint.id, startDate, endDate, user?.email, token]);

  useEffect(() => {
    void load();
  }, [load]);

  // Escape-to-close for keyboard users.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const stats = data?.stats ?? EMPTY_STATS;
  const meta = data?.checkpoint ?? checkpoint;
  const scans = data?.scans ?? [];

  const complianceColor = useMemo(() => {
    if (stats.compliance >= 90) return "text-emerald-400";
    if (stats.compliance >= 70) return "text-amber-400";
    return "text-rose-400";
  }, [stats.compliance]);

  return (
    <div className="fixed inset-0 z-[1000] flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative h-full w-full max-w-2xl bg-white dark:bg-slate-900 shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        {/* Navy header with gold accent */}
        <header className="relative bg-slate-900 px-6 py-5 border-b-2 border-amber-400">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
            aria-label={t("common.close", "Close")}
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex items-center gap-2 mb-1">
            <MapPin className="h-5 w-5 text-amber-400" />
            <h2 className="text-xl font-bold text-white tracking-tight pr-8">
              {meta.name || t("checkpoints.untitled", "Untitled Checkpoint")}
            </h2>
          </div>
          {meta.hospital_name && (
            <p className="text-xs text-slate-400 ml-7 mb-3">{meta.hospital_name}</p>
          )}

          <div className="flex flex-wrap gap-x-5 gap-y-2 ml-7 text-xs">
            {meta.code && (
              <span className="flex items-center gap-1.5 text-slate-300">
                <QrCode className="h-3.5 w-3.5 text-amber-400/80" />
                <span className="font-mono">{meta.code}</span>
              </span>
            )}
            {meta.building && (
              <span className="flex items-center gap-1.5 text-slate-300">
                <Building2 className="h-3.5 w-3.5 text-amber-400/80" />
                {meta.building}
              </span>
            )}
            {meta.floor && (
              <span className="flex items-center gap-1.5 text-slate-300">
                <Layers className="h-3.5 w-3.5 text-amber-400/80" />
                {t("home.floorN", "Floor {n}").replace("{n}", String(meta.floor))}
              </span>
            )}
          </div>
        </header>

        {/* Stats strip */}
        <div className="grid grid-cols-4 divide-x divide-slate-700 bg-slate-800 text-center">
          <Stat label={t("checkpoints.totalScans", "Total Scans")} value={String(stats.total)} valueClass="text-white" />
          <Stat
            label={t("checkpoints.compliance", "Compliance")}
            value={`${stats.compliance}%`}
            valueClass={complianceColor}
          />
          <Stat label={t("checkpoints.late", "Late")} value={String(stats.late)} valueClass="text-amber-400" />
          <Stat label={t("checkpoints.missed", "Missed")} value={String(stats.missed)} valueClass="text-rose-400" />
        </div>

        {/* Date range filter */}
        <div className="flex flex-wrap items-end gap-3 px-6 py-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50/70 dark:bg-slate-800/40">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <Calendar className="h-3 w-3" /> {t("checkpoints.fromDate", "From")}
            </label>
            <input
              type="date"
              value={startDate}
              max={endDate || undefined}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 outline-none focus:ring-2 focus:ring-amber-400/40"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
              <Calendar className="h-3 w-3" /> {t("checkpoints.toDate", "To")}
            </label>
            <input
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 outline-none focus:ring-2 focus:ring-amber-400/40"
            />
          </div>
          {(startDate || endDate) && (
            <button
              type="button"
              onClick={() => {
                setStartDate("");
                setEndDate("");
              }}
              className="text-xs font-semibold text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline pb-2"
            >
              {t("checkpoints.clearDates", "Clear")}
            </button>
          )}
          <button
            type="button"
            onClick={() => void load()}
            className="ml-auto inline-flex items-center gap-2 rounded-lg bg-slate-900 dark:bg-slate-700 px-4 py-1.5 text-sm font-bold text-white hover:bg-slate-800 active:scale-95 transition-all"
          >
            <RefreshCw className={cn("h-4 w-4 text-amber-400", loading && "animate-spin")} />
            {t("common.refresh", "Refresh")}
          </button>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-500">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-amber-400 border-t-transparent" />
              <p className="text-sm">{t("checkpoints.loadingHistory", "Loading scan history…")}</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 gap-2 text-center">
              <AlertTriangle className="h-8 w-8 text-rose-400" />
              <p className="text-sm font-medium text-rose-600">{error}</p>
            </div>
          ) : scans.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-2 text-center text-gray-500">
              <MapPin className="h-8 w-8 text-gray-300" />
              <p className="text-sm">{t("checkpoints.noScans", "No scans recorded for this checkpoint in the selected range.")}</p>
            </div>
          ) : (
            <ol className="relative border-l-2 border-slate-200 dark:border-slate-700 ml-2">
              {scans.map((scan) => (
                <TimelineItem key={scan.id} scan={scan} t={t} lang={lang} />
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, valueClass }: { label: string; value: string; valueClass: string }) {
  return (
    <div className="py-3 px-2">
      <p className={cn("text-xl font-bold leading-none", valueClass)}>{value}</p>
      <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
    </div>
  );
}

function TimelineItem({ scan, t, lang }: { scan: HistoryScan; t: TFn; lang: LangCode }) {
  const cfg = STATUS_CONFIG[scan.status];
  const StatusIcon = cfg.icon;
  return (
    <li className="ml-6 mb-5">
      <span
        className={cn(
          "absolute -left-[9px] flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-white dark:ring-slate-900",
          cfg.dot
        )}
      />
      <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {formatDateTime(scan.scanned_at, lang)}
            </p>
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
              <Smartphone className="h-3.5 w-3.5 text-gray-400" />
              {scan.device_name || t("checkpoints.unknownDevice", "Unknown device")}
              {scan.guard_id && <span className="text-gray-400">· {scan.guard_id}</span>}
            </p>
          </div>
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-bold whitespace-nowrap",
              cfg.badge
            )}
          >
            <StatusIcon className="h-3 w-3" />
            {t(cfg.labelKey, cfg.labelFallback)}
          </span>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
          {scan.gps_lat != null && scan.gps_lng != null ? (
            <a
              href={`https://www.google.com/maps?q=${scan.gps_lat},${scan.gps_lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-mono text-blue-600 dark:text-blue-400 hover:underline"
            >
              <Navigation className="h-3 w-3" />
              {scan.gps_lat.toFixed(5)}, {scan.gps_lng.toFixed(5)}
            </a>
          ) : (
            <span className="text-gray-400">{t("checkpoints.noGps", "No GPS")}</span>
          )}
          {scan.proximity_distance_m != null && (
            <span
              className={cn(
                "font-semibold",
                scan.proximity_distance_m > 100 ? "text-rose-600" : "text-emerald-600"
              )}
            >
              {Math.round(scan.proximity_distance_m)}m
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

const STATUS_CONFIG: Record<
  ScanStatus,
  {
    icon: typeof CheckCircle2;
    dot: string;
    badge: string;
    labelKey: string;
    labelFallback: string;
  }
> = {
  ON_TIME: {
    icon: CheckCircle2,
    dot: "bg-emerald-500",
    badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
    labelKey: "checkpoints.statusOnTime",
    labelFallback: "On time",
  },
  LATE: {
    icon: AlertTriangle,
    dot: "bg-amber-500",
    badge: "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300",
    labelKey: "checkpoints.statusLate",
    labelFallback: "Late",
  },
  MISSED: {
    icon: XCircle,
    dot: "bg-rose-500",
    badge: "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300",
    labelKey: "checkpoints.statusMissed",
    labelFallback: "Missed",
  },
};

function formatDateTime(ts: string, lang: LangCode) {
  return new Intl.DateTimeFormat(lang, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(ts));
}
